/* eslint-disable */
/**
 * Visio Calendar - FullCalendar Integration
 * Follows same patterns as Tabulator implementation
 *
 * @package WPCore
 */

import { loadTemplate, templateStore } from "../template/index.js";
import { getResolvedAppLang } from "../utils/cookie";

/**
 * Calendar color schemes based on session state
 */
const COLOR_SCHEMES = {
	// Blue: Completed OR Past sessions
	past: {
		textColor: "#0369A1",
		iconBgColor: "#0EA5E9",
		bgColor: "#E7F6FD",
		borderColor: "#0EA5E9",
	},
	// Green: Today's sessions (not completed)
	today: {
		textColor: "#067647",
		iconBgColor: "#17B26A",
		bgColor: "#ECFDF3",
		borderColor: "#17B26A",
	},
	// Orange: Future sessions
	future: {
		textColor: "#B54708",
		iconBgColor: "#F79009",
		bgColor: "#FFFAEB",
		borderColor: "#F79009",
	},
	// Red: Cancelled sessions
	canceled: {
		textColor: "#B42318",
		iconBgColor: "#F04438",
		bgColor: "#FEF3F2",
		borderColor: "#F04438",
	},
};

/**
 * Get date status and colors based on date, time and canceled flag
 * @param {Date|string} dateInput - Date object or string in YYYYMMDD format
 * @param {string} timeStr - Time string in HH:mm:ss format (optional)
 * @param {boolean} canceled - Whether session is canceled
 * @returns {Object} { key, label, textColor, iconBgColor, bgColor, borderColor }
 */
function getDateStatus(dateInput, timeStr = "", canceled = false) {
	// Check canceled first
	if (canceled) {
		return {
			key: "canceled",
			label: wpcore.direct_t({ en: 'Cancelled', fr: 'Annul\u00e9e' }),
			...COLOR_SCHEMES.canceled,
		};
	}

	let sessionDate;

	// Handle Date object or string input
	if (dateInput instanceof Date) {
		sessionDate = new Date(dateInput);
	} else if (typeof dateInput === "string" && dateInput.length === 8) {
		const year = parseInt(dateInput.substring(0, 4), 10);
		const month = parseInt(dateInput.substring(4, 6), 10) - 1;
		const day = parseInt(dateInput.substring(6, 8), 10);
		sessionDate = new Date(year, month, day);
	} else {
		// Invalid input, return upcoming as default
		return {
			key: "upcoming",
			label: wpcore.direct_t({ en: 'Upcoming', fr: '\u00c0 venir' }),
			...COLOR_SCHEMES.future,
		};
	}

	const today = new Date();
	const now = new Date();

	sessionDate.setHours(0, 0, 0, 0);
	today.setHours(0, 0, 0, 0);

	// Past date
	if (sessionDate < today) {
		return {
			key: "past",
			label: wpcore.direct_t({ en: 'Completed', fr: 'Termin\u00e9e' }),
			...COLOR_SCHEMES.past,
		};
	}

	// Today - check time if provided
	if (sessionDate.getTime() === today.getTime()) {
		if (timeStr) {
			const [h, m] = timeStr.split(":").map((n) => parseInt(n, 10) || 0);
			if (
				h < now.getHours() ||
				(h === now.getHours() && m < now.getMinutes())
			) {
				return {
					key: "past",
					label: wpcore.direct_t({ en: 'Completed', fr: 'Termin\u00e9e' }),
					...COLOR_SCHEMES.past,
				};
			}
		}
		// Today but not past time - use today colors
		return {
			key: "upcoming",
			label: wpcore.direct_t({ en: 'Upcoming', fr: '\u00c0 venir' }),
			...COLOR_SCHEMES.today,
		};
	}

	// Future
	return {
		key: "upcoming",
		label: wpcore.direct_t({ en: 'Upcoming', fr: '\u00c0 venir' }),
		...COLOR_SCHEMES.future,
	};
}

/**
 * Build FullCalendar configuration
 * @param {Array} eventsData - Events data
 * @returns {Object} FullCalendar config
 */
function buildCalendarConfig(eventsData) {
	return {
		locale: getResolvedAppLang(),
		initialView: "dayGridMonth",
		headerToolbar: {
			left: "prev,next today",
			center: "title",
			right: "dayGridMonth,timeGridWeek",
		},
		buttonText: {
			today: wpcore.direct_t({ en: 'Today', fr: 'Aujourd\'hui' }),
			month: wpcore.direct_t({ en: 'Month', fr: 'Mois' }),
			week: wpcore.direct_t({ en: 'Week', fr: 'Semaine' }),
		},
		allDaySlot: false,
		slotMinTime: "07:00:00",
		slotMaxTime: "18:00:00",
		slotDuration: "00:30:00",
		slotLabelInterval: "01:00:00",
		scrollTime: "08:00:00",
		events: eventsData,
		dayMaxEvents: 2,
		eventColor: "#BF7C5A",
		eventDisplay: "auto",
		displayEventTime: true,
		displayEventEnd: false,
		height: "auto",
		aspectRatio: 1.8,
		slotMinWidth: 142,

		moreLinkContent: (args) => `+ ${args.num} ${wpcore.direct_t({ en: 'more', fr: 'plus' })}`,

		moreLinkClick: () => {
			const listViewBtn = document.getElementById("view-toggle-list");
			if (listViewBtn) {
				listViewBtn.click();
			}
			return "none";
		},

		eventClick: handleEventClick,
		eventContent: renderEventContent,
		eventDidMount: applyEventStyles,
	};
}

/**
 * Handle event click - fetch and show session detail
 * @param {Object} info - FullCalendar event info
 */
function handleEventClick(info) {
	const sessionId = info.event.id;

	if (!sessionId || typeof wpcore === "undefined") {
		return;
	}

	const formData = new FormData();
	formData.append("action", "wpcore_get_session_detail");
	formData.append("nonce", wpcore.nonce);
	formData.append("session_id", sessionId);

	fetch(wpcore.ajaxUrl, {
		method: "POST",
		body: formData,
	})
		.then((response) => response.json())
		.then((result) => {
			if (result.success) {
				try {
					const data = result.data;
					window.currentSessionDetail = data;
					renderSessionDetailPopup(data);
					updateSessionButtons(data);

					// Show popup using wpcore helper
					if (typeof wpcore !== "undefined" && wpcore.showPopup) {
						wpcore.showPopup("session-detail");
					}
				} catch (error) {
					// Silent fail
				}
			}
		})
		.catch(() => {
			// Silent fail
		});
}

/**
 * Render custom event content using EJS template
 * @param {Object} arg - FullCalendar event arg
 * @returns {Object} DOM nodes for event
 */
function renderEventContent(arg) {
	const isTimeGridView = arg.view.type === "timeGridWeek";
	const student = arg.event.extendedProps.student || "N/A";
	const duration = arg.event.extendedProps.duration || 0;
	const canceled = arg.event.extendedProps.canceled || false;
	const eventDate = arg.event.start;
	const status = getDateStatus(eventDate, "", canceled);

	const template = templateStore.get("visio/calendar-event.ejs");
	if (!template) {
		return { html: "" };
	}

	const html = ejs.render(template, {
		eventDate,
		student,
		duration,
		textColor: status.textColor,
		iconBgColor: status.iconBgColor,
		isTimeGridView,
	});

	return { html: html };
}

/**
 * Apply custom styles to event element
 * @param {Object} info - FullCalendar event info
 */
function applyEventStyles(info) {
	const canceled = info.event.extendedProps.canceled || false;
	const status = getDateStatus(info.event.start, "", canceled);

	info.el.style.backgroundColor = status.bgColor;
	info.el.style.borderLeft = `3px solid ${status.borderColor}`;
	info.el.style.borderColor = status.borderColor;
	info.el.style.paddingLeft = "4px";
	info.el.style.borderRadius = "4px";

	const allTextElements = info.el.querySelectorAll("div, span");
	allTextElements.forEach((el) => {
		el.style.color = status.textColor;
	});
}

/**
 * Initialize view toggle handlers
 * Handle switching between calendar and list views
 * @param {Object} calendar - FullCalendar instance
 */
function initViewToggle(calendar) {
	const calendarBtn = document.getElementById("view-toggle-calendar");
	const listBtn = document.getElementById("view-toggle-list");
	const calendarView = document.getElementById("calendar-view");
	const listView = document.getElementById("list-view");

	if (!calendarBtn || !listBtn || !calendarView || !listView) {
		return;
	}

	function switchView(view) {
		if (view === "calendar") {
			calendarView.classList.remove("hidden");
			listView.classList.add("hidden");
			calendarBtn.classList.add("active");
			listBtn.classList.remove("active");

			// Update calendar size after showing
			setTimeout(() => {
				calendar.updateSize();
			}, 100);
		} else {
			calendarView.classList.add("hidden");
			listView.classList.remove("hidden");
			calendarBtn.classList.remove("active");
			listBtn.classList.add("active");
		}

		localStorage.setItem("visio-view-preference", view);
	}

	calendarBtn.addEventListener("click", () => switchView("calendar"));
	listBtn.addEventListener("click", () => switchView("list"));

	// Start with calendar view
	switchView("calendar");
}

/**
 * Initialize Visio Calendar
 * Main export function - follows tabulator pattern
 */
export const initVisioCalendar = async () => {
	const calendarEl = document.getElementById("visio-calendar");
	if (!calendarEl) return;

	// Load templates
	await Promise.all([
		loadTemplate("visio/calendar-event.ejs"),
		loadTemplate("visio/session-detail-content.ejs"),
	]);

	// Get events data from data attribute
	const eventsDataAttr = calendarEl.getAttribute("data-events");
	const eventsData = eventsDataAttr ? JSON.parse(eventsDataAttr) : [];

	// Check if FullCalendar is loaded
	if (typeof FullCalendar === "undefined") {
		return;
	}

	// Create and render calendar
	const config = buildCalendarConfig(eventsData);
	const calendar = new FullCalendar.Calendar(calendarEl, config);
	calendar.render();

	// Setup view toggle
	initViewToggle(calendar);

	// Setup save link handler
	initSaveLinkHandler();

	// Setup send note handler
	initSendNoteHandler();

	// Setup join session handler
	initJoinSessionHandler();

	// Setup card join handler (for list view cards)
	initCardJoinHandler();
};

// ============================================================
// Session Detail Popup Functions
// ============================================================

/**
 * Render session detail popup using EJS template
 * @param {Object} sessionData - Session data from API
 */
function renderSessionDetailPopup(sessionData) {
	// Determine status based on date, time and canceled flag
	const dateStatus = getDateStatus(
		sessionData.date || "",
		sessionData.time || "",
		sessionData.canceled || false,
	);

	const session = {
		student: sessionData.student || {},
		status: dateStatus.label,
		topic: sessionData.topic || "",
		date: formatDateToReadable(sessionData.date || ""),
		time: sessionData.time || "",
		format: sessionData.format || "",
		notes: sessionData.notes || "",
	};

	// Update popup content directly using IDs
	const avatarEl = document.getElementById("session-detail-avatar");
	const nameEl = document.getElementById("session-detail-student-name");
	const statusEl = document.getElementById("session-detail-status");
	const topicEl = document.getElementById("session-detail-topic");
	const dateEl = document.getElementById("session-detail-date");
	const timeEl = document.getElementById("session-detail-time");
	const formatEl = document.getElementById("session-detail-format");

	if (avatarEl) avatarEl.src = session.student?.avatar || "";
	if (nameEl) nameEl.textContent = session.student?.name || "N/A";
	if (topicEl) topicEl.textContent = session.topic || "";
	if (dateEl) dateEl.textContent = session.date || "";
	if (timeEl) timeEl.textContent = session.time || "";
	if (formatEl) formatEl.textContent = session.format || "";

	// Update status badge based on date
	if (statusEl) {
		const statusColors = {
			past: "bg-success-50 text-success-500",
			upcoming: "bg-primary-50 text-primary-500",
			canceled: "bg-error-50 text-error-500",
		};

		const statusKey = dateStatus.key;
		const colorClass = statusColors[statusKey] || "bg-gray-50 text-gray-500";
		statusEl.innerHTML = `
			<div class="px-2 py-1 ${colorClass} rounded-full">
				<p class="mb-0 text-xs font-medium">${session.status}</p>
			</div>
		`;
	}

	// Update coach notes
	const notesEl = document.getElementById("coach_notes");
	if (notesEl) {
		notesEl.value = sessionData.notes || "";
		// Set readonly if notes already exist
		notesEl.readOnly = !!sessionData.notes;
	}

	// Update link input
	const linkInputEl = document.getElementById("session_link");
	if (linkInputEl) {
		linkInputEl.value = sessionData.join_url || "";
	}
}

/**
 * Update session popup buttons based on status
 * Uses server-calculated flags for timezone consistency
 * @param {Object} sessionData - Session data from API
 */
function updateSessionButtons(sessionData) {
	const elements = {
		sendNoteBtn: document.getElementById("session-popup-send-note"),
		rescheduleBtn: document.getElementById("session-popup-reschedule"),
		joinBtn: document.getElementById("session-popup-join"),
		cancelBtn: document.getElementById("session-popup-cancel"),
		notesWrapper: document.getElementById("coach_notes_wrapper"),
		notesTextarea: document.getElementById("coach_notes"),
		todayLabel: document.getElementById("today-label"),
		linkInput: document.getElementById("link_input"),
	};

	if (!sessionData) return;

	// Hide all by default (except notesWrapper and notesTextarea which are inside wrapper)
	Object.values(elements).forEach((el) => {
		if (
			el &&
			el.style &&
			el !== elements.notesWrapper &&
			el !== elements.notesTextarea
		) {
			el.style.display = "none";
		}
	});

	// Ensure notes wrapper is hidden (uses hidden class)
	if (elements.notesWrapper) elements.notesWrapper.classList.add("hidden");

	// Use server-calculated flags (wp_timezone consistency)
	const isPast = sessionData.is_past || false;
	const isUpcoming = sessionData.is_upcoming || false;
	const isToday = sessionData.is_today || false;
	const canJoin = sessionData.can_join || false;
	const is24hBefore = sessionData.is_24h_before || false;
	const isCanceled = sessionData.canceled || false;

	// Show today label if applicable
	if (isToday && elements.todayLabel) {
		elements.todayLabel.style.display = "inline-flex";
	}

	// Show buttons based on status
	if (isPast) {
		// Past session: show notes
		if (elements.notesWrapper) {
			elements.notesWrapper.classList.remove("hidden");
			// Only show send note button if notes is empty
			if (elements.sendNoteBtn && !sessionData.notes) {
				elements.sendNoteBtn.style.display = "block";
			}
		}
	} else if (isUpcoming) {
		// Upcoming session
		// Show link input
		if (elements.linkInput) elements.linkInput.style.display = "block";

		// Join button: always visible, disabled when !canJoin
		if (elements.joinBtn) {
			elements.joinBtn.style.display = "flex";
			if (canJoin) {
				elements.joinBtn.disabled = false;
				elements.joinBtn.classList.remove("opacity-50", "cursor-not-allowed");
				elements.joinBtn.classList.add("cursor-pointer");
			} else {
				elements.joinBtn.disabled = true;
				elements.joinBtn.classList.add("opacity-50", "cursor-not-allowed");
				elements.joinBtn.classList.remove("cursor-pointer");
			}
		}

		// Reschedule: show when 24h before
		if (is24hBefore && elements.rescheduleBtn) {
			elements.rescheduleBtn.style.display = "block";
		}

		// Cancel: show when 24h before AND not canceled
		if (is24hBefore && !isCanceled && elements.cancelBtn) {
			elements.cancelBtn.style.display = "block";
		}
	}
}

/**
 * Parse session date/time to timestamp
 * @param {string} dateStr - Date string in YYYYMMDD format
 * @param {string} timeStr - Time string in HH:mm format
 * @returns {number} Timestamp in milliseconds, 0 if invalid
 */
function parseSessionTimestamp(dateStr, timeStr) {
	if (!dateStr || dateStr.length !== 8) return 0;

	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1;
	const day = parseInt(dateStr.substring(6, 8), 10);

	let hours = 0;
	let minutes = 0;

	if (timeStr) {
		const timeParts = timeStr.split(":");
		hours = parseInt(timeParts[0], 10) || 0;
		minutes = parseInt(timeParts[1], 10) || 0;
	}

	const date = new Date(year, month, day, hours, minutes, 0, 0);
	return date.getTime();
}

// ============================================================
// Utility Functions
// ============================================================

/**
 * Check if date string is today
 * @param {string} dateStr - Date string in YYYYMMDD format
 * @returns {boolean}
 */
function checkIfToday(dateStr) {
	if (!dateStr || dateStr.length !== 8) return false;

	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1;
	const day = parseInt(dateStr.substring(6, 8), 10);

	const sessionDate = new Date(year, month, day);
	const today = new Date();

	sessionDate.setHours(0, 0, 0, 0);
	today.setHours(0, 0, 0, 0);

	return sessionDate.getTime() === today.getTime();
}

/**
 * Format date from YYYYMMDD to readable format
 * @param {string} dateStr - Date string in YYYYMMDD format
 * @returns {string} Formatted date
 */
function formatDateToReadable(dateStr) {
	if (!dateStr || dateStr.length !== 8) return dateStr;

	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1;
	const day = parseInt(dateStr.substring(6, 8), 10);

	const date = new Date(year, month, day);

	const locale = wpcore.direct_t({ en: "en-US", fr: "fr-FR" });
	return date.toLocaleDateString(locale, {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

/**
 * Initialize save link button handler
 */
function initSaveLinkHandler() {
	const saveLinkBtn = document.getElementById("save-link-btn");
	if (!saveLinkBtn) return;

	saveLinkBtn.addEventListener("click", () => {
		const linkInput = document.getElementById("session_link");
		if (!linkInput) return;

		const link = linkInput.value.trim();
		const sessionData = window.currentSessionDetail;

		if (!sessionData || !sessionData.id) {
			return;
		}

		// Disable button while saving
		saveLinkBtn.disabled = true;
		saveLinkBtn.textContent = wpcore.direct_t({ en: 'Saving...', fr: 'Enregistrement...' });

		const formData = new FormData();
		formData.append("action", "wpcore_update_session_link");
		formData.append("nonce", wpcore.nonce);
		formData.append("session_id", sessionData.id);
		formData.append("link", link);

		fetch(wpcore.ajaxUrl, {
			method: "POST",
			body: formData,
		})
			.then((response) => response.json())
			.then((result) => {
				saveLinkBtn.textContent = wpcore.direct_t({ en: 'Save', fr: 'Enregistrer' });
				saveLinkBtn.disabled = false;

				if (result.success) {
					window.currentSessionDetail.join_url = link;
					wpcore.showAlert("success", wpcore.direct_t({ en: 'Success', fr: 'Succ\u00e8s' }), wpcore.direct_t({ en: 'Link saved successfully!', fr: 'Lien enregistr\u00e9 avec succ\u00e8s !' }));
				} else {
					wpcore.showAlert(
						"error",
						wpcore.direct_t({ en: 'Error', fr: 'Erreur' }),
						wpcore.direct_t({ en: 'Failed to save link. Please try again.', fr: '\u00c9chec de l\'enregistrement du lien. Veuillez r\u00e9essayer.' }),
					);
				}
			})
			.catch(() => {
				saveLinkBtn.textContent = wpcore.direct_t({ en: 'Save', fr: 'Enregistrer' });
				saveLinkBtn.disabled = false;
				wpcore.showAlert(
					"error",
					wpcore.direct_t({ en: 'Error', fr: 'Erreur' }),
					wpcore.direct_t({ en: 'An error occurred. Please try again.', fr: 'Une erreur est survenue. Veuillez r\u00e9essayer.' }),
				);
			});
	});
}

/**
 * Initialize send note button handler
 */
function initSendNoteHandler() {
	const sendNoteBtn = document.getElementById("session-popup-send-note");
	if (!sendNoteBtn) return;

	sendNoteBtn.addEventListener("click", () => {
		const notesTextarea = document.getElementById("coach_notes");
		if (!notesTextarea) return;

		const coachNotes = notesTextarea.value.trim();
		const sessionData = window.currentSessionDetail;

		if (!sessionData || !sessionData.id) {
			return;
		}

		// Disable button while saving
		sendNoteBtn.disabled = true;
		sendNoteBtn.textContent = wpcore.direct_t({ en: 'Sending...', fr: 'Envoi...' });

		const formData = new FormData();
		formData.append("action", "wpcore_update_coach_notes");
		formData.append("nonce", wpcore.nonce);
		formData.append("session_id", sessionData.id);
		formData.append("coach_notes", coachNotes);

		fetch(wpcore.ajaxUrl, {
			method: "POST",
			body: formData,
		})
			.then((response) => response.json())
			.then((result) => {
				sendNoteBtn.textContent = wpcore.direct_t({ en: 'Send Note', fr: 'Envoyer la note' });
				sendNoteBtn.disabled = false;

				if (result.success) {
					window.currentSessionDetail.notes = coachNotes;
					wpcore.showAlert("success", wpcore.direct_t({ en: 'Success', fr: 'Succ\u00e8s' }), wpcore.direct_t({ en: 'Note sent to student!', fr: 'Note envoy\u00e9e \u00e0 l\'\u00e9tudiant !' }));
				} else {
					wpcore.showAlert(
						"error",
						wpcore.direct_t({ en: 'Error', fr: 'Erreur' }),
						result.data?.message || wpcore.direct_t({ en: 'Failed to send note. Please try again.', fr: '\u00c9chec de l\'envoi de la note. Veuillez r\u00e9essayer.' }),
					);
				}
			})
			.catch(() => {
				sendNoteBtn.textContent = wpcore.direct_t({ en: 'Send Note', fr: 'Envoyer la note' });
				sendNoteBtn.disabled = false;
				wpcore.showAlert(
					"error",
					wpcore.direct_t({ en: 'Error', fr: 'Erreur' }),
					wpcore.direct_t({ en: 'An error occurred. Please try again.', fr: 'Une erreur est survenue. Veuillez r\u00e9essayer.' }),
				);
			});
	});
}

/**
 * Initialize join session button handler
 */
function initJoinSessionHandler() {
	const joinBtn = document.getElementById("session-popup-join");
	if (!joinBtn) return;

	joinBtn.addEventListener("click", () => {
		// Get link from input first, fallback to stored join_url
		const linkInput = document.getElementById("session_link");
		const inputValue = linkInput ? linkInput.value.trim() : "";
		const sessionData = window.currentSessionDetail;
		const joinUrl = inputValue || sessionData?.join_url;

		if (!joinUrl) {
			wpcore.showAlert("error", wpcore.direct_t({ en: 'Error', fr: 'Erreur' }), wpcore.direct_t({ en: 'No meeting link available.', fr: 'Aucun lien de r\u00e9union disponible.' }));
			return;
		}

		// Open join_url in new tab
		window.open(joinUrl, "_blank");
	});
}

/**
 * Initialize card join button handler (for list view cards)
 * Uses event delegation on list-view container
 */
function initCardJoinHandler() {
	const listView = document.getElementById("list-view");
	if (!listView) return;

	listView.addEventListener("click", (e) => {
		const joinBtn = e.target.closest('[data-action="join"]');
		if (!joinBtn) return;

		// Check if button is disabled
		if (joinBtn.disabled) return;

		const joinUrl = joinBtn.getAttribute("data-join-url");
		if (!joinUrl) {
			if (typeof wpcore !== "undefined" && wpcore.showAlert) {
				wpcore.showAlert("error", wpcore.direct_t({ en: 'Error', fr: 'Erreur' }), wpcore.direct_t({ en: 'No meeting link available.', fr: 'Aucun lien de r\u00e9union disponible.' }));
			}
			return;
		}

		window.open(joinUrl, "_blank");
	});
}
